// VELLA 2.0 — i18n PROVIDER
// Lightweight in-house i18n system. No react-i18next dep — translations
// are static JSON, the active language lives in React context, and
// callers reach for it via the useTranslation() hook.
//
// Why hand-rolled: the surface area we need is small (lookup by dot key
// + interpolation + lang switching), and a 200-line context module avoids
// pulling in ~80 KB of i18next + plugins for a v1 of multi-language
// support. Swap to react-i18next later if pluralization rules or ICU
// formatting become real requirements.
//
// Public API:
//   <I18nProvider>          — wrap the app once near the root
//   useTranslation()        — { t, language, setLanguage, languages }
//   detectInitialLanguage() — pure helper exported for tests
//
// Storage: the active language is persisted to localStorage under
// `vella_language`. On first visit we read navigator.language and pick
// the closest supported language (falling back to 'en').
//
// HTTP: api.js reads getCurrentLanguage() to send Accept-Language and a
// `language` field on AI requests so the brain can reply in the user's
// language. The two stay in sync via the same module-scoped variable.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import en from "./en.json";
import es from "./es.json";
import fr from "./fr.json";
import pt from "./pt.json";

// ─── Supported languages ───────────────────────────────────────────────
// Order here drives the language selector UI in Settings.
export const LANGUAGES = [
  { code: "en", label: "English",    nativeLabel: "English" },
  { code: "es", label: "Spanish",    nativeLabel: "Español" },
  { code: "fr", label: "French",     nativeLabel: "Français" },
  { code: "pt", label: "Portuguese", nativeLabel: "Português" },
];

const TRANSLATIONS = { en, es, fr, pt };
const STORAGE_KEY = "vella_language";
const DEFAULT_LANGUAGE = "en";

// ─── Module-scoped current language ────────────────────────────────────
// Mirrored on the provider. Exists so non-React code (api.js) can read
// the active language without subscribing to context.
let _currentLanguage = DEFAULT_LANGUAGE;

export function getCurrentLanguage() {
  return _currentLanguage;
}

// ─── Detection ─────────────────────────────────────────────────────────
// Picks the best supported language given (1) a saved preference,
// (2) navigator.language (and navigator.languages), or (3) the default.
export function detectInitialLanguage() {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  try {
    const saved = window.localStorage?.getItem(STORAGE_KEY);
    if (saved && TRANSLATIONS[saved]) return saved;
  } catch {
    // localStorage may be blocked (Safari private mode) — fall through
  }

  const candidates = [];
  if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
  if (navigator.language) candidates.push(navigator.language);

  for (const raw of candidates) {
    if (!raw) continue;
    // navigator.language values look like "es-419", "fr-CA", "pt-BR" — we
    // only care about the base language tag.
    const base = String(raw).toLowerCase().split(/[-_]/)[0];
    if (TRANSLATIONS[base]) return base;
  }
  return DEFAULT_LANGUAGE;
}

// ─── Lookup ────────────────────────────────────────────────────────────
// Walks a dot path into a nested translations object. Missing keys fall
// back to English so a partial translation file never renders blank.
function lookup(dict, key) {
  if (!key) return undefined;
  const parts = String(key).split(".");
  let cur = dict;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) {
      cur = cur[p];
    } else {
      return undefined;
    }
  }
  return cur;
}

// {{name}} interpolation. Missing vars stay as the literal placeholder so
// a typo is visible instead of silently dropping content.
function interpolate(template, vars) {
  if (typeof template !== "string" || !vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) =>
    vars[key] !== undefined && vars[key] !== null ? String(vars[key]) : m,
  );
}

// ─── Context ───────────────────────────────────────────────────────────
const I18nContext = createContext(null);

export function I18nProvider({ children, initialLanguage }) {
  const [language, setLanguageState] = useState(
    () => initialLanguage || detectInitialLanguage(),
  );

  // Keep module-scoped mirror + <html lang> + persisted preference in sync.
  useEffect(() => {
    _currentLanguage = language;
    try { window.localStorage?.setItem(STORAGE_KEY, language); } catch { /* private mode */ }
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = (next) => {
    if (!TRANSLATIONS[next]) {
      console.warn(`[i18n] Unsupported language "${next}" — ignoring.`);
      return;
    }
    setLanguageState(next);
  };

  const value = useMemo(() => {
    const dict = TRANSLATIONS[language] || TRANSLATIONS[DEFAULT_LANGUAGE];
    const fallback = TRANSLATIONS[DEFAULT_LANGUAGE];

    // t(key, vars?) — looks up a key, falls back to English, then to the
    // raw key string so a missing translation is debuggable on screen.
    function t(key, vars) {
      const hit = lookup(dict, key);
      if (typeof hit === "string") return interpolate(hit, vars);
      const fb = lookup(fallback, key);
      if (typeof fb === "string") return interpolate(fb, vars);
      return key;
    }

    return {
      t,
      language,
      setLanguage,
      languages: LANGUAGES,
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// ─── Hook ──────────────────────────────────────────────────────────────
// Always returns a usable object — even if a caller is rendered outside
// the provider we degrade to identity translation rather than throwing.
// This keeps storybook / unit-test rendering simple.
export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (ctx) return ctx;
  return {
    t: (key) => key,
    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},
    languages: LANGUAGES,
  };
}
